import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 30;

const actionColors = {
  delete_user: 'destructive',
  delete_campaign: 'destructive',
  update_user: 'secondary',
  reset_password: 'secondary',
  update_settings: 'default',
  test_email: 'outline',
  archive_campaign: 'secondary',
  unarchive_campaign: 'secondary',
  cleanup_invites: 'outline',
  cleanup_tokens: 'outline',
};

const AdminAuditLogsPage = () => {
  const { language } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/audit-logs', {
        params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, action_filter: filter },
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/api/admin/export/audit-logs', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit_logs_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('error_generic', language));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold font-cinzel">{t('admin_audit_logs', language)}</h1>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            {t('admin_export_csv', language)}
          </Button>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <Input
            placeholder={t('admin_filter_action', language)}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button type="submit" size="icon" variant="secondary">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('admin_audit_date', language)}</th>
                    <th className="text-left p-3 font-medium">{t('admin_audit_admin', language)}</th>
                    <th className="text-left p-3 font-medium">{t('admin_audit_action', language)}</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">{t('admin_audit_target', language)}</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">{t('admin_audit_details', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-border">
                      <td className="p-3 text-muted-foreground whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </td>
                      <td className="p-3">{log.admin_username || log.admin_id?.slice(0, 8)}</td>
                      <td className="p-3">
                        <Badge variant={actionColors[log.action] || 'outline'}>
                          {formatAction(log.action)}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground hidden sm:table-cell">
                        {log.target_type && (
                          <span>
                            {log.target_type}
                            {log.target_id && ` (${log.target_id.slice(0, 8)}...)`}
                          </span>
                        )}
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        {log.details && Object.keys(log.details).length > 0 && (
                          <code className="text-xs bg-muted p-1 rounded">
                            {Object.entries(log.details)
                              .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                              .join(', ')}
                          </code>
                        )}
                      </td>
                    </tr>
                  ))}
                  {logs.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-muted-foreground">
                        {t('no_results', language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{total} {t('admin_count_entries', language)}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLogsPage;
